import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { Feature, GeoData } from '../../../models/geo-data';
import { MenuItem } from 'primeng/api';
import { getState, patchState } from '@ngrx/signals';
import { ButtonModule } from 'primeng/button';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { NgForOf, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { HeaderComponent } from '../../atoms/header/header.component';
import { districtKeyValidator } from '../../../validators/district-key.validator';
import { checkUniquenessValidator } from '../../../validators/uniqueFormArrayProperty.validator';
import { checkDifferencesValidator } from '../../../validators/differenceFormArray.validator';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { GeoJSON } from 'leaflet';
import { DataLayer } from '../../../models/data-layer';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { PanelModule } from 'primeng/panel';
import { MenuModule } from 'primeng/menu';
import { MaskComponent } from '../../organisms/mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { FeatureMetaFormComponent } from '../feature-meta-form/feature-meta-form.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { AppUtilityService } from '../../../utility/app.utility';
import { ToastModule } from 'primeng/toast';
import { PaginatorModule } from 'primeng/paginator';
import { DataViewModule } from 'primeng/dataview';
import { YearListComponent } from '../year-list/year-list.component';
import { CheckboxModule } from 'primeng/checkbox';
import { PermissionService } from '../../../services/permission.service';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'app-feature-form',
  standalone: true,
  imports: [
    ButtonModule,
    FormFieldWrapperComponent,
    ReactiveFormsModule,
    NgForOf,
    InputTextModule,
    DividerModule,
    AlertStyleDirective,
    HeaderComponent,
    NgIf,
    ScrollPanelModule,
    IconFieldModule,
    InputIconModule,
    SearchInputComponent,
    PanelModule,
    MenuModule,
    MaskComponent,
    SidebarModule,
    FeatureMetaFormComponent,
    AlertComponent,
    ToastModule,
    PaginatorModule,
    DataViewModule,
    YearListComponent,
    CheckboxModule,
    TableModule,
  ],
  templateUrl: './feature-form.component.html',
  styleUrl: './feature-form.component.scss',
})
export class FeatureFormComponent implements OnChanges, OnInit, OnDestroy {
  featureForm: FormGroup = new FormGroup({});
  readonly geoDataStore = inject(GeoDataStore);
  readonly globalStore = inject(GlobalsStore);
  readonly layerStore = inject(DataLayerStore);
  @Output() submitted: EventEmitter<void> = new EventEmitter<void>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();
  @Output() triggerConvert: EventEmitter<void> = new EventEmitter<void>();
  @Input() type: 'Point' | 'Polygon' = 'Polygon';
  @Input() editMenuAvailable = true;
  @Input() activeImport = false;
  @Input() convertAvailable = false;
  @Input() selectedLayer: DataLayer | null = null;
  @Input() inputFeatures?: GeoJSON.Feature<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  >[];

  initialized = false;
  currentFeatures: GeoJSON.Feature<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  >[] = this.inputFeatures || [];
  selectedGeoData: GeoData | null = null;
  filteredFeatures?: FormArray;
  items: MenuItem[] = [];
  featureToEdit?: AbstractControl;
  layerSiblings: DataLayer[] = [];
  sharedGeoDataLayers: DataLayer[] = [];
  overrideLayersByDifferences = false;
  public ignoreDifferentKeysValidator = false;
  public isReadonly = false;

  constructor(
    private fb: FormBuilder,
    private appUtilityService: AppUtilityService,
    private permissionService: PermissionService
  ) {
    // store effects
    effect(() => {
      const geoState = getState(this.geoDataStore);
      const globalState = getState(this.globalStore);
      const layerState = getState(this.layerStore);

      this.selectedGeoData = this.geoDataStore.selectedItem();
      this.checkSharedLayers(layerState.selection);

      if (geoState.initialized && layerState.initialized) this.renderForm();
    });
  }

  ngOnInit() {
    this.init();
  }

  /**
   * Responds to changes to the input properties of the component.
   * This method is called whenever Angular detects input property changes.
   *
   * @param {SimpleChanges} changes - Object of changes. Each changed property is represented as a key,
   *                                   and its value contains metadata about the change,
   *                                   including previous and current values.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['inputFeatures'] ||
      changes['selectedLayer'] ||
      changes['activeImport']
    ) {
      this.init();
    }
  }

  /**
   * Initializes the component by managing layer selection, sibling layers, shared geo-data layers, and rendering the form.
   *
   * This method handles the following:
   * - Updates the geo-data selection in the geoDataStore if a layer with geo-data exists.
   * - If the current type is 'Point' and the selected layer matches, retrieves sibling layers and shared geo-data layers, then updates the layer selection in the layerStore.
   * - Clears the layer selection in the layerStore if the above conditions are not met.
   * - Triggers the rendering of the form.
   *
   * @return {void} No return value.
   */
  init(): void {
    const layerType = this.globalStore.selectedType();
    if (!layerType) return;

    if (this.selectedLayer && this.selectedLayer.geoData) {
      this.geoDataStore.singleSelection(this.selectedLayer.geoData);
    } else {
      this.geoDataStore.singleSelection('');
    }

    this.isReadonly = !this.permissionService.checkDataLayerRights(
      layerType._id,
      this.activeImport ? 'import' : 'edit',
      false
    );


    if (this.isReadonly) {
    } else {
      this.renderForm();
    }
    if (
      this.type === 'Point' &&
      this.selectedLayer &&
      this.selectedLayer.type === 'Point'
    ) {
      this.layerSiblings = this.layerStore.getSiblings(this.selectedLayer);
      if (this.selectedLayer.geoData) {
        this.sharedGeoDataLayers = this.layerStore.getLayersByGeoData(
          this.selectedLayer.geoData
        );
        this.layerStore.updateSelection([...this.sharedGeoDataLayers]);
      } else {
        this.layerStore.updateSelection([this.selectedLayer]);
      }
    } else {
      this.layerStore.clearSelection();
    }

    this.renderForm();
  }

  /**
   * Opens a context menu for a specific feature at a given index with associated event.
   *
   * @param {MouseEvent} event - The mouse event that triggered the menu opening.
   * @param {number} index - The index of the feature in the list.
   * @param {AbstractControl} feature - The control object representing the feature to be interacted with.
   * @return {void} This method does not return a value.
   */
  openMenu(event: MouseEvent, index: number, feature: AbstractControl): void {
    this.items = [
      {
        label: 'Aktionen',
        items: [
          {
            visible: this.globalStore.selectedType()?.type === 'Point',
            label: 'bearbeiten',
            icon: 'fas fa-pen',
            command: () => this.editFeature(feature, index),
          },
          {
            label: 'löschen',
            icon: 'fas fa-trash',
            command: () => this.removeFeature(index),
          },
        ],
      },
    ];
  }

  /**
   * Initializes and renders a reactive form for managing features with validation.
   * The form is configured with a FormArray to handle multiple features and includes
   * custom validators for feature uniqueness and differences based on geo data.
   *
   * This method also resets the form, filters its features, and populates the form
   * with either provided input features or features from the geo data store.
   *
   * @return {void} Does not return a value.
   */
  renderForm(): void {
    this.featureForm = this.fb.group({
      features: this.fb.array([], {
        validators: [
          checkUniquenessValidator,
          checkDifferencesValidator(this.geoDataStore.keysFromSelection()),
        ],
      }),
    });

    this.filteredFeatures = this.featureForm.get('features') as FormArray;
    this.filteredControls = this.filteredFeatures.controls;

    this.resetForm();
    if (this.features) {
      if (this.activeImport) {
        this.currentFeatures = this.inputFeatures || [];
      } else {
        this.currentFeatures =
          this.inputFeatures || this.geoDataStore.featuresFromSelection();
      }
      this.currentFeatures.forEach(feature => {
        this.addFeature(feature);
      });
    }
    this.initialized = true;
  }

  /**
   * Retrieves the 'features' form array from the feature form.
   *
   * @return {FormArray} The form array that represents the 'features' control from the form.
   */
  get features(): FormArray {
    return this.featureForm.get('features') as FormArray;
  }

  /**
   * Resets the form by clearing any stored features.
   *
   * @return {void} Does not return any value.
   */
  resetForm(): void {
    if (this.features) this.features.clear();
  }

  /**
   * Adds a GeoJSON feature to the feature list with its properties and geometry information.
   *
   * @param {GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>} [feature] - The GeoJSON feature to be added, which can optionally include properties and geometry information.
   * @return {void} This method does not return a value.
   */
  addFeature(feature?: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon>): void {
    const featureGroup = this.fb.group({
      type: ['Feature'],
      properties: this.fb.group({
        key: [
          {
            value: feature?.properties?.['key'] || '',
            disabled: !!feature?.properties?.['key'] && !this.inputFeatures,
          },
          Validators.required /*,
          districtKeyValidator(
            !this.selectedLayer && this.inputFeatures
              ? this.geoDataStore.keysFromSelection()
              : undefined
          ),*/,
        ],
        name: [feature?.properties?.['name'] || '', Validators.required],
        group: [feature?.properties?.['group'] || ''],
        address: [feature?.properties?.['address'] || ''],
        information: [feature?.properties?.['information'] || ''],
        contact: [feature?.properties?.['contact'] || ''],
        description: [feature?.properties?.['description'] || ''],
      }),
      geometry: this.fb.group({
        coordinates: [feature?.geometry?.coordinates || []],
        type: [feature?.geometry?.type || this.type],
      }),
    });
    this.features.push(featureGroup);
  }

  /**
   * Retrieves the feature name associated with the given key.
   *
   * @param {string} key - The unique identifier used to find the corresponding feature name.
   * @return {string | undefined} The name of the feature associated with the provided key, or undefined if no matching feature is found.
   */
  getFeatureNameByKey(key: string): string | undefined {
    return this.geoDataStore
      .featuresFromSelection()
      .find((feature: Feature) => {
        return feature.properties.key === key;
      })?.properties.name;
  }

  /**
   * Removes a feature from the features list at the specified index.
   *
   * @param {number} index - The zero-based index of the feature to be removed.
   * @return {void} This method does not return a value.
   */
  removeFeature(index: number): void {
    this.features.removeAt(index);
  }

  /**
   * Submits the form data by generating geo-data based on the form's features
   * and performing either an update or addition operation depending on the
   * selected geo-data. Provides success messages upon completion and emits
   * a submitted event.
   *
   * @return {void} No return value.
   */
  submitForm(): void {
    const geoData = this.geoDataStore.generateByFeatures(
      this.featureForm.value.features,
      this.currentFeatures // todo - why is this merged? -> ignore for poi -> no poi-deletation possible
    );

    this.appUtilityService.markAllAsDirty(this.featureForm);

    if (this.type === 'Point' || this.ignoreDifferentKeysValidator) {
      const featuresControl = this.featureForm.get('features');
      featuresControl?.clearValidators(); // Remove all validators
      featuresControl?.addValidators([checkUniquenessValidator]); // Retain other necessary validators
      featuresControl?.updateValueAndValidity();
    }

    if (this.featureForm.valid) {
      geoData.poiJson = this.type === 'Point';
      const stateUpdates: any = {
        triggerOptions: { dataLayer: this.selectedLayer },
      };
      if (this.type === 'Point' && this.overrideLayersByDifferences) {
        stateUpdates.triggerOptions.dataLayers = [
          ...this.layerStore.selection(),
        ];
      } else {
        delete stateUpdates.triggerOptions.dataLayers;
      }

      patchState(this.geoDataStore, stateUpdates);

      if (this.selectedGeoData) {
        geoData._id =
          this.type === 'Point' && this.overrideLayersByDifferences
            ? ''
            : this.selectedGeoData._id;
        geoData.message = {
          severity: 'success',
          summary: 'Änderungen erfolgreich!',
          detail: 'Standorte wurden aktualisiert.',
        };

        if (geoData._id) {
          this.geoDataStore.update(geoData);
        } else {
          this.geoDataStore.addItem(geoData);
        }
        this.submitted.emit();
        this.renderForm();
      } else {
        geoData.message = {
          severity: 'success',
          summary: 'Import erfolgreich!',
          detail:
            this.type === 'Point'
              ? 'Standorte wurden erfolgreich hinzugefügt!'
              : 'Teilräume wurden erfolgreich hinzugefügt!',
        };
        this.geoDataStore.addItem(geoData);
        this.submitted.emit();
        //this.renderForm();
        this.initialized = false;
      }
    }
  }

  filteredControls: AbstractControl[] = [];

  onSearch(searchTerm: string): void {
    const featuresArray = this.featureForm.get('features') as FormArray;

    if (!searchTerm) {
      // Reset to show all controls
      this.filteredControls = featuresArray.controls;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();

      this.filteredControls = featuresArray.controls.filter(featureControl => {
        const properties = featureControl.get('properties') as FormGroup;

        return Object.values(properties.controls).some(control => {
          return (
            control.value &&
            (control.value as string).toLowerCase().includes(lowerSearchTerm)
          );
        });
      });
    }
  }

  /**
   * closes meta-form
   */
  closeMetaForm() {
    this.featureToEdit = undefined;
  }

  /**
   * edit feature in meta-form
   * @param feature
   * @param i
   */
  editFeature(feature: AbstractControl, i: number) {
    this.featureToEdit = feature;
  }

  /**
   * update feature from meta-form
   * @param form
   */
  updateFeature(form: FormGroup) {
    if (this.featureToEdit) {
      this.featureToEdit.patchValue(form.value);
      this.closeMetaForm();
    }
  }

  /**
   * Checks the difference between the currently selected layers and the shared geo-data layers.
   * Updates the state indicating if there are overriding layers due to these differences.
   *
   * @param {DataLayer[]} newSelection - An array of data layers representing the new selection.
   * @return {void} This method does not return a value.
   */
  private checkSharedLayers(newSelection: DataLayer[]): void {
    if (!this.selectedLayer) return;
    const diffLayers = [
      ...newSelection.filter(
        layer => !this.sharedGeoDataLayers.includes(layer)
      ),
      ...this.sharedGeoDataLayers.filter(
        layer => !newSelection.includes(layer)
      ),
    ];

    this.overrideLayersByDifferences = diffLayers.length > 0;
  }

  /**
   * Constructs a descriptive header text based on the type and input features.
   *
   * @return {string} A string representing the header text. It indicates whether the object type is 'Point' or not,
   *                  and specifies if the information is sourced from an import or a data layer.
   */
  get headerText(): string {
    return `Übersicht der ${this.type === 'Point' ? 'Standorte' : 'Teilräume'}`;
    //return `Übersicht der ${this.type === 'Point' ? 'Standorte' : 'Teilräume'}${this.inputFeatures ? ' aus dem Import' : ' aus der Datenschicht'}`;
  }

  cancel(): void {
    this.inputFeatures = this.geoDataStore.featuresFromSelection();
    this.cancelled.emit();
  }

  ngOnDestroy() {
    this.cancel();
    this.featureForm.reset();
    this.layerStore.clearSelection();
  }
}
